import { cn } from "@/lib/utils";
import {
  OptimizedCartResponse,
} from "../types/optimizedCart";
import { OptimizedCartCard } from "./optimizedCartCard";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Bell, LockKeyhole, Sparkle, WandSparkles } from "lucide-react";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import { useMediaQuery } from "@/hooks/use-media-query";
import { isDesktopSize } from "utils/is-desktop";
import { useNavigate, useParams } from "react-router-dom";
import { useFetch } from "modules/main/hooks/useFetch";
import { endPointOptimizedResults } from "modules/main/services/endpoints";
import { FetchDataOptions } from "modules/main/services/fetchData";
import { useAuth } from "modules/authentication/hooks/use-auth";
import { getVisitorId } from "utils/analytics";
import posthog from "posthog-js";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { usePwaInstall } from "modules/main/hooks/use-pwa-install";
import { MainLogo } from "modules/main/assets/main_logo";
import { PageBreadcrumbs } from "modules/main/components/page-breadcrumbs";
import { usePageTitle } from "hooks/usePageTitle";
import { useConnectedAccountsValidation } from "modules/checkout/hooks/useConnectedAccountsValidation";
import { ConnectedAccountsDialog } from "./ConnectedAccountsDialog";
import { MainFlowSteps } from "modules/lists/components/main-flow-steps";

const CREATE_CART_EVENT_CODE = "Crear carrito";
const VIEW_DETAIL_EVENT_CODE = "Ver detalle";
const SHOULD_SHOW_NOTIFICATION_PERMISSION_PROMPT = false;

interface NotificationPermissionToastProps {
  onRequestPermission: () => Promise<NotificationPermission | "unsupported">;
}

const NotificationPermissionToast = ({
  onRequestPermission,
}: NotificationPermissionToastProps) => {
  const [isRequestingPermission, setIsRequestingPermission] = useState(false);

  const onEnableNotifications = async () => {
    try {
      setIsRequestingPermission(true);
      await onRequestPermission();
    } finally {
      setIsRequestingPermission(false);
    }
  };

  return (
    <div className="w-full rounded-lg border bg-popover px-4 py-3 text-popover-foreground shadow-lg">
      <div className="flex flex-col gap-3">
        <div className="flex items-end gap-2">
          <MainLogo classname="h-5 w-5" />
          <p className="text-sm font-extrabold">
            ¿Quieres recibir notificaciones de Carriapp?
          </p>
        </div>
        <p className="text-xs text-muted-foreground text-center">
          Activa alertas para enterarte al instante de novedades.
        </p>
        <Button
          className="w-full"
          disabled={isRequestingPermission}
          onClick={() => {
            void onEnableNotifications();
          }}
          size="sm"
          type="button"
          asChild
        >
          <div>
            <Bell className="w-4 h-4 mr-2" />
            <span>
              {isRequestingPermission
                ? "Solicitando permiso..."
                : "Activar notificaciones"}
            </span>
          </div>
        </Button>
      </div>
    </div>
  );
};

export const ScenarioResults = () => {
  const { isLogged, requireLoginAction } = useAuth();
  const { notificationPermission, requestNotificationPermission } = usePwaInstall();
  const { setBreadcrumb, setShowBack} = usePageTitle();

  const hasShownNotificationPromptRef = useRef(false);
  const [pendingCartContinuation, setPendingCartContinuation] = useState<(() => void) | null>(null);
  const notificationToastIdRef = useRef<string | number | null>(null);
  const requestOptions: FetchDataOptions = {
    method: "GET",
    headers: {
      "content-type": "application/json",
      ...(!isLogged ? { "x-visitor-id": getVisitorId() } : {}),
    },
  };
  const isDesktop = useMediaQuery(isDesktopSize);
  const navigate = useNavigate();
  const {
    isValidating,
    disconnectedStores,
    isDialogOpen,
    setIsDialogOpen,
    validateBeforeCreateCart,
  } = useConnectedAccountsValidation();
  const { order_id: orderIdParam } = useParams<{ order_id: string }>();
  const order_id = Number.isFinite(Number(orderIdParam))
    ? Number(orderIdParam)
    : 0;

  // The breadcrumb replaces the h2 title (HeaderBar precedence) and the back
  // arrow is hidden — the breadcrumb itself provides navigation.
  useEffect(()=>{
    setBreadcrumb(
      <PageBreadcrumbs
        crumbs={[
          { label: "Inicio", to: "/store" },
          { label: "Tu Compra Optimizada" },
        ]}
      />,
    );
    setShowBack(false);
    return () => setBreadcrumb(null);
  },[setBreadcrumb]);  
  useEffect(() => {
    if (
      !SHOULD_SHOW_NOTIFICATION_PERMISSION_PROMPT ||
      notificationPermission !== "default" ||
      hasShownNotificationPromptRef.current ||
      !isLogged
    ) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      if (hasShownNotificationPromptRef.current) {
        return;
      }

      hasShownNotificationPromptRef.current = true;
      notificationToastIdRef.current = toast.custom(
        () => (
          <NotificationPermissionToast
            onRequestPermission={requestNotificationPermission}
          />
        ),
        {
          closeButton: true,
          duration: 10000,
          position: "bottom-center",
        },
      );
    }, 5000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [notificationPermission, requestNotificationPermission]);

  useEffect(() => {
    if (
      notificationPermission !== "granted" ||
      notificationToastIdRef.current === null
    ) {
      return;
    }

    toast.dismiss(notificationToastIdRef.current);
    notificationToastIdRef.current = null;
  }, [notificationPermission]);

  // console.log("Order id for fetching optimized scenarios: ", order_id);
  const { data, error, loading } = useFetch<OptimizedCartResponse>(
    endPointOptimizedResults(order_id),
    requestOptions,
  );

  if (loading) {
    return (
      <>
        <div className="flex flex-row w-full gap-5 py-4 px-6 lg:p-8 flex-wrap">
          <div
            className={cn("flex w-full flex-col gap-4", {
              "w-full": !isDesktop,
              "lg:w-[80%] xl:w-[80%] 2xl:w-[60%] mx-auto": isDesktop,
            })}
          >
            <Skeleton className="h-6 w-40" />
            <div className="w-full rounded-lg border p-4 space-y-4">
              <div className="flex items-start gap-4">
                <Skeleton className="h-10 w-10 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-40" />
                  <Skeleton className="h-3 w-full" />
                  <Skeleton className="h-3 w-4/5" />
                </div>
              </div>
              <div className="flex gap-2">
                <Skeleton className="h-9 w-36" />
                <Skeleton className="h-9 w-28" />
              </div>
            </div>

            <Skeleton className="h-4 w-28 mt-2" />
            <div
              className={cn("flex gap-6", {
                "flex-wrap gap-6 justify-between": isDesktop,
                "flex-col": !isDesktop,
              })}
            >
              {Array.from({ length: 3 }).map((_, index) => (
                <div
                  key={index}
                  className={cn("rounded-lg border p-4 space-y-3", {
                    "w-full": !isDesktop,
                    "w-[30%] max-w-[30%]": isDesktop,
                  })}
                >
                  <Skeleton className="h-4 w-28" />
                  <Skeleton className="h-3 w-full" />
                  <Skeleton className="h-3 w-5/6" />
                  <Skeleton className="h-9 w-24" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </>
    );
  }
  // console.log("Optimized scenarios data: ", data);
  if (
    data === undefined ||
    !data ||
    data.scenarios === undefined ||
    data.scenarios.length === 0
  ) {
    return (
      <>
        <div className="flex flex-row w-full gap-5 py-4 px-6 lg:p-8 flex-wrap">
          <div
            className={cn("flex w-full flex-col gap-2", {
              "w-full": !isDesktop,
              "lg:w-[80%] xl:w-[80%] 2xl:w-[60%] mx-auto": isDesktop,
            })}
          >
            <Empty className="my-10">
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  <Sparkle className="size-5" />
                </EmptyMedia>
                <EmptyTitle>No encontramos opciones para tu carrito</EmptyTitle>
                <EmptyDescription>
                  Intenta ajustar los productos o vuelve a optimizar para
                  obtener más resultados.
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          </div>
        </div>
      </>
    );
  }

  const onViewDetails = (scenarioId: number, storeId?: number) => {
    //navigate to details page with scenarioId as parameter
    if (!requireLoginAction()) {
      return;
    }
    posthog.capture(VIEW_DETAIL_EVENT_CODE);
    navigate(
      `/cart/results/${order_id}/${scenarioId}/${storeId ? storeId : ""}`,
    );
  };

  const continueToCartCreation = (scenarioId: number, storeId?: number) => {
    posthog.capture(CREATE_CART_EVENT_CODE);
    navigate(
      "/cart/create_cart?scenario_id=" +
        scenarioId +
        (storeId ? `&store_id=${storeId}` : ""),
    );
  };

  const onCreateCart = async (scenarioId: number, storeId?: number) => {
    if (!requireLoginAction()) {
      return;
    }

    setPendingCartContinuation(() => () => continueToCartCreation(scenarioId, storeId));
    const canNavigate = await validateBeforeCreateCart({ scenarioId, storeId });
    if (!canNavigate) {
      return;
    }

    setPendingCartContinuation(null);
    continueToCartCreation(scenarioId, storeId);
  };

  const handleConnectedAccountsOpenChange = (open: boolean) => {
    setIsDialogOpen(open);
    if (!open) {
      setPendingCartContinuation(null);
    }
  };

  const handleCreateCartsFromConnectedAccounts = () => {
    pendingCartContinuation?.();
    setPendingCartContinuation(null);
  };

  const sortedScenarios = (data?.scenarios ?? [])
    .map((scenario, index) => ({ scenario, index }))
    .sort((left, right) => {
      if (left.scenario.reccomended === right.scenario.reccomended) {
        return left.index - right.index;
      }

      return left.scenario.reccomended ? -1 : 1;
    })
    .map(({ scenario }) => scenario);

  return (
    <>
      <div className="flex flex-row w-full gap-5 py-4 px-6  lg:p-8 flex-wrap">
        {/* Full-width wrapper: the stepper occupies its OWN row above the
            content. As a direct flex item of the flex-row container it would
            render as a narrow column, pushing the content to its right on
            desktop and leaving it left-aligned on mobile. On desktop the
            wrapper mirrors the content column's centering
            (lg:w-[80%] xl:w-[80%] 2xl:w-[60%] mx-auto) so the stepper row is
            centered under the centered content column. */}
        <div
          className={cn("w-full", {
            "lg:w-[80%] xl:w-[80%] 2xl:w-[60%] mx-auto": isDesktop,
          })}
        >
          <MainFlowSteps currentStep={3} />
        </div>
        <div
          className={cn("flex w-full flex-col gap-2", {
            "w-full": !isDesktop,
            "lg:w-[80%] xl:w-[80%] 2xl:w-[60%] mx-auto": isDesktop,
          })}
        >
          <h2 className="font-semibold">Te recomendamos</h2>
          {sortedScenarios
            .filter((scenario) => scenario.reccomended)
            .map((scenario, index) => {
              const createCartAction = async () => {
                if (!requireLoginAction()) {
                  return;
                }
                const storeId =
                  scenario.scenarioType === "UNIQUE_STORE"
                    ? scenario.store_resume[0]?.id
                    : undefined;
                await onCreateCart(scenario.order_id, storeId);
              };
              return (
                <div className="flex flex-col gap-1 w-full " key={index}>
                  <OptimizedCartCard
                    scenario={scenario}
                    viewDetailsCallBack={onViewDetails}
                    createCartCallBack={onCreateCart}
                    validationLoading={isValidating}
                  />
                  {scenario.reccomended && !isDesktop && (
                    <div className="flex flex-col gap-2 pt-2 w-full items-center">
                      <Button
                        className="w-full"
                        size={"lg"}
                        onClick={() => {
                          createCartAction();
                        }}
                      >
                        {isLogged ? (
                          <WandSparkles className="w-4 h-4 mr-2" />
                        ) : (
                          <LockKeyhole className="w-4 h-4 mr-2" />
                        )}
                        Crea tu Carrito
                      </Button>
                      <p className="text-center text-sm text-muted-foreground">
                        Creamos los carritos directo en la app del súper.
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          {sortedScenarios.filter((scenario) => !scenario.reccomended).length >
            0 && (
            <>
              <h2 className="font-semibold text-sm mt-4">Otras opciones</h2>
              <div
                className={cn("flex gap-6", {
                  "flex-wrap gap-6 justify-between": isDesktop,
                  "flex-col": !isDesktop,
                })}
              >
                {sortedScenarios
                  .filter((scenario) => !scenario.reccomended)
                  .map((scenario, index) => {
                    return (
                      <div
                        className={cn("flex flex-col gap-6 w-full", {
                        })}
                        key={index}
                      >
                        <OptimizedCartCard
                          scenario={scenario}
                          viewDetailsCallBack={onViewDetails}
                          createCartCallBack={onCreateCart}
                          validationLoading={isValidating}
                          compact={false}
                        />
                      </div>
                    );
                  })}
              </div>
            </>
          )}
        </div>
      </div>
      <ConnectedAccountsDialog
        open={isDialogOpen}
        onOpenChange={handleConnectedAccountsOpenChange}
        disconnectedStores={disconnectedStores}
        onCreateCarts={handleCreateCartsFromConnectedAccounts}
      />
    </>
  );
};
